// @ts-check
// Etapa 2 (doc 14, 9.2 "selectHeader" e "matchColumns"): linha do cabeçalho, coluna -> campo com sugestão
// automática, valores de seleção sem opção correspondente e, nas relações, se cria o registro que faltar.
import { html } from 'htm/preact';
import { useMemo } from 'preact/hooks';
import { Icon } from '../../ui/icon.js';
import { Checkbox } from '../../ui/feedback.js';
import { objectsByNameSingular } from '../../data/metadata-store.js';
import { t, translateMetadataLabel } from '../../i18n/index.js';
import { findUnmatchedSelectValues, groupTargetsForSelect, headerRowChoices, targetLabel } from './import-model.js';

const SAMPLE_VALUES = 3;
// "deixar em branco" no seletor; no mapeamento vira texto vazio
const BLANK_VALUE = '__blank__';
// relações que a importação nunca cria a partir de um texto (mesma regra do motor)
const NEVER_CREATED_OBJECTS = new Set(['workspaceMember']);

/**
 * @typedef {import('./import-model.js').ImportTarget} ImportTarget
 * @param {{ mode: import('./import-model.js').ImportMode, rows: string[][], headerRowIndex: number, onHeaderRowChange: (index: number) => void,
 *   targets: ImportTarget[], mapping: Array<string | null>, onMappingChange: (columnIndex: number, targetKey: string | null) => void,
 *   valueMappings: Record<number, Record<string, string>>, onValueMappingChange: (columnIndex: number, normalized: string, value: string) => void,
 *   createMissing: Set<string>, onCreateMissingChange: (targetKey: string, value: boolean) => void }} props
 */
export function MatchColumnsStep({
  mode, rows, headerRowIndex, onHeaderRowChange, targets, mapping, onMappingChange, valueMappings, onValueMappingChange, createMissing, onCreateMissingChange,
}) {
  const headers = rows[headerRowIndex] ?? [];
  const dataRows = useMemo(() => rows.slice(headerRowIndex + 1), [rows, headerRowIndex]);
  const targetsByKey = useMemo(() => new Map(targets.map((target) => [target.key, target])), [targets]);
  const groups = useMemo(() => groupTargetsForSelect(mode, targets), [mode, targets]);
  const matchedCount = mapping.filter(Boolean).length;
  const usedKeys = new Set(mapping.filter(Boolean));
  const missingRequired = mode === 'object' ? targets.filter((target) => target.isRequired && !usedKeys.has(target.key)) : [];

  return html`<div class="spreadsheet-import-match">
    <div class="spreadsheet-import-step-heading">
      <div>
        <h3 class="spreadsheet-import-step-title">${t('spreadsheetImport.match.title')}</h3>
        <p class="spreadsheet-import-step-subtitle">${t('spreadsheetImport.match.subtitle')}</p>
      </div>
      <label class="spreadsheet-import-inline-field">
        <span>${t('spreadsheetImport.match.headerRow')}</span>
        <select class="spreadsheet-import-select" value=${String(headerRowIndex)}
          onChange=${(/** @type {Event} */ event) => onHeaderRowChange(Number(/** @type {HTMLSelectElement} */ (event.currentTarget).value))}>
          ${headerRowChoices(rows).map((choice) => html`<option value=${String(choice.index)} key=${choice.index}>
            ${t('spreadsheetImport.match.headerRowOption', { number: choice.index + 1, preview: choice.preview })}
          </option>`)}
        </select>
      </label>
    </div>
    <div class="spreadsheet-import-match-summary">${t('spreadsheetImport.match.matchedCount', { count: matchedCount, total: headers.length })}</div>
    ${missingRequired.length > 0
      ? html`<div class="spreadsheet-import-banner" data-variant="warning"><${Icon} name="IconAlertTriangle" size=${16} />
          ${t('spreadsheetImport.match.requiredMissing', { fields: missingRequired.map(targetLabel).join(', ') })}</div>`
      : null}
    <div class="spreadsheet-import-match-table" role="table">
      <div class="spreadsheet-import-match-row spreadsheet-import-match-head" role="row">
        <span role="columnheader">${t('spreadsheetImport.match.column')}</span>
        <span role="columnheader">${t('spreadsheetImport.match.sample')}</span>
        <span role="columnheader">${t('spreadsheetImport.match.field')}</span>
      </div>
      ${headers.map((header, columnIndex) => {
        const targetKey = mapping[columnIndex] ?? null;
        const target = targetKey ? targetsByKey.get(targetKey) : undefined;
        const samples = dataRows.map((row) => String(row[columnIndex] ?? '').trim()).filter(Boolean).slice(0, SAMPLE_VALUES);
        return html`<div class="spreadsheet-import-match-row" role="row" key=${columnIndex} data-matched=${target ? 'true' : undefined}>
          <span class="spreadsheet-import-match-header" role="cell" title=${header}>${header || `#${columnIndex + 1}`}</span>
          <span class="spreadsheet-import-match-samples" role="cell" title=${samples.join(' · ')}>${samples.join(' · ')}</span>
          <span class="spreadsheet-import-match-target" role="cell">
            <select class="spreadsheet-import-select" aria-label=${`${t('spreadsheetImport.match.field')}: ${header}`} value=${targetKey ?? ''}
              onChange=${(/** @type {Event} */ event) => onMappingChange(columnIndex, /** @type {HTMLSelectElement} */ (event.currentTarget).value || null)}>
              <option value="">${t('spreadsheetImport.match.doNotImport')}</option>
              ${groups.map((group) => html`<optgroup label=${group.label} key=${group.label}>
                ${group.targets.map((option) => html`<option value=${option.key} key=${option.key}
                  disabled=${usedKeys.has(option.key) && option.key !== targetKey}>${targetLabel(option)}</option>`)}
              </optgroup>`)}
            </select>
            ${target ? html`<${TargetDetails} target=${target} columnIndex=${columnIndex} dataRows=${dataRows}
              valueMapping=${valueMappings[columnIndex] ?? {}} onValueMappingChange=${onValueMappingChange}
              createMissing=${createMissing} onCreateMissingChange=${onCreateMissingChange} />` : null}
          </span>
        </div>`;
      })}
    </div>
  </div>`;
}

/**
 * @param {{ target: ImportTarget, columnIndex: number, dataRows: string[][], valueMapping: Record<string, string>,
 *   onValueMappingChange: (columnIndex: number, normalized: string, value: string) => void,
 *   createMissing: Set<string>, onCreateMissingChange: (targetKey: string, value: boolean) => void }} props
 */
function TargetDetails({ target, columnIndex, dataRows, valueMapping, onValueMappingChange, createMissing, onCreateMissingChange }) {
  const unmatched = useMemo(
    () => (target.kind === 'SELECT' || target.kind === 'MULTI_SELECT' ? findUnmatchedSelectValues(dataRows, columnIndex, target) : []),
    [target, dataRows, columnIndex],
  );
  if (target.kind === 'RELATION') {
    const relatedObject = target.relationTargetObjectNameSingular ? objectsByNameSingular.value.get(target.relationTargetObjectNameSingular) : undefined;
    const canCreate = relatedObject && !NEVER_CREATED_OBJECTS.has(relatedObject.nameSingular);
    return html`<div class="spreadsheet-import-target-details">
      <span class="spreadsheet-import-hint">${t('spreadsheetImport.match.relationHint')}</span>
      ${canCreate
        ? html`<label class="spreadsheet-import-check">
            <${Checkbox} checked=${createMissing.has(target.key)} onChange=${(/** @type {boolean} */ value) => onCreateMissingChange(target.key, value)} />
            <span>${t('spreadsheetImport.match.createMissing', { object: translateMetadataLabel(relatedObject.labelSingular).toLowerCase() })}</span>
          </label>`
        : null}
    </div>`;
  }
  if (unmatched.length === 0) return null;
  const options = target.field.options ?? [];
  return html`<div class="spreadsheet-import-target-details">
    <span class="spreadsheet-import-hint">${t('spreadsheetImport.match.unmatchedValues')}</span>
    ${unmatched.map(([normalized, original]) => html`<label class="spreadsheet-import-value-mapping" key=${normalized}>
      <span class="spreadsheet-import-value-original" title=${original}>${original}</span>
      <${Icon} name="IconArrowRight" size=${14} />
      <select class="spreadsheet-import-select" value=${normalized in valueMapping ? valueMapping[normalized] || BLANK_VALUE : ''}
        onChange=${(/** @type {Event} */ event) => {
          const value = /** @type {HTMLSelectElement} */ (event.currentTarget).value;
          onValueMappingChange(columnIndex, normalized, value === BLANK_VALUE ? '' : value);
        }}>
        <option value="" disabled>${t('spreadsheetImport.match.chooseValue')}</option>
        <option value=${BLANK_VALUE}>${t('spreadsheetImport.match.ignoreValue')}</option>
        ${options.map((option) => html`<option value=${option.value} key=${option.value}>${translateMetadataLabel(option.label)}</option>`)}
      </select>
    </label>`)}
  </div>`;
}
